import { useMemo } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import type { ShareObject } from '../types/ShareObject';
import getShareUrlPath from '../utils/getShareUrlPath';

type UseShareUrl = {
  baseUrl: string;
  shareUrl: string;
};

/**
 * Builds the canonical link for a share object, with the attribution params every share channel expects.
 */
export default function useShareUrl(shareObject: ShareObject): UseShareUrl {
  const currentUser = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const shareUrl = useMemo(() => {
    const params = new URLSearchParams(shareObject.searchParams);

    if (currentUser) {
      params.set('u', currentUser.metric ? 'm' : 'i');
      params.set('sh', currentUser.referralCode || '');
    } else {
      // Identifies share links from unauthenticated users for virality attribution
      params.set('sh', 'true');
    }

    return baseUrl + wrapUrlSafe(`/${getShareUrlPath(shareObject)}?${params.toString()}`, languageRegionCode);
  }, [baseUrl, currentUser, languageRegionCode, shareObject]);

  return { baseUrl, shareUrl };
}
